import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  XMarkIcon,
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon,
  ArrowRightIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { ServiceItem, ServiceItemHistory } from '../types';
import { proposalsApi } from '../services/api';

interface ItemHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ServiceItem;
}

const ACTION_ICONS = {
  'created': CheckCircleIcon,
  'rented': ArrowRightIcon,
  'returned': ArrowRightIcon,
  'maintenance_start': WrenchScrewdriverIcon,
  'maintenance_end': WrenchScrewdriverIcon,
  'damaged': ExclamationTriangleIcon,
  'repaired': CheckCircleIcon,
  'retired': XMarkIcon,
  'location_change': MapPinIcon,
  'status_change': ClockIcon
};

const ACTION_COLORS = {
  'created': 'text-green-600 bg-green-100',
  'rented': 'text-blue-600 bg-blue-100',
  'returned': 'text-blue-600 bg-blue-100',
  'maintenance_start': 'text-yellow-600 bg-yellow-100',
  'maintenance_end': 'text-green-600 bg-green-100',
  'damaged': 'text-red-600 bg-red-100',
  'repaired': 'text-green-600 bg-green-100',
  'retired': 'text-gray-600 bg-gray-100',
  'location_change': 'text-purple-600 bg-purple-100',
  'status_change': 'text-blue-600 bg-blue-100'
};

export default function ItemHistoryModal({ isOpen, onClose, item }: ItemHistoryModalProps) {
  const [history, setHistory] = useState<ServiceItemHistory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && item.id) {
      fetchItemHistory();
    }
  }, [isOpen, item.id]);

  const fetchItemHistory = async () => {
    setLoading(true);
    try {
      const response = await proposalsApi.getServiceItemHistory(item.id);
      setHistory(response.results || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action: string) => {
    const IconComponent = ACTION_ICONS[action as keyof typeof ACTION_ICONS] || DocumentTextIcon;
    return IconComponent;
  };

  const getActionColor = (action: string) => {
    return ACTION_COLORS[action as keyof typeof ACTION_COLORS] || 'text-gray-600 bg-gray-100';
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      Histórico do Item
                    </Dialog.Title>
                    <p className="text-xs text-gray-500 mt-1">
                      {item.internal_code} - {item.service_name}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Informações Atuais do Item */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Status Atual</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <p className="font-medium">{item.status_display}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Localização:</span>
                      <p className="font-medium">{item.current_location || 'Não informado'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">QR Code:</span>
                      <p className="font-medium font-mono text-xs">{item.qr_code}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Disponível para locação:</span>
                      <p className="font-medium">{item.is_available_for_rent ? 'Sim' : 'Não'}</p>
                    </div>
                  </div>
                  {item.condition_notes && (
                    <div className="mt-3">
                      <span className="text-gray-500 text-sm">Observações:</span>
                      <p className="text-sm mt-1">{item.condition_notes}</p>
                    </div>
                  )}
                </div>

                {/* Histórico */}
                <div className="max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-500 mt-2">Carregando histórico...</p>
                    </div>
                  ) : history.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum histórico encontrado</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        Este item ainda não possui registros de atividade.
                      </p>
                    </div>
                  ) : (
                    <div className="flow-root">
                      <ul className="-mb-8">
                        {history.map((entry, entryIdx) => {
                          const ActionIcon = getActionIcon(entry.action);
                          const actionColor = getActionColor(entry.action);
                          
                          return (
                            <li key={entry.id}>
                              <div className="relative pb-8">
                                {entryIdx !== history.length - 1 ? (
                                  <span
                                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                                    aria-hidden="true"
                                  />
                                ) : null}
                                <div className="relative flex space-x-2">
                                  <div>
                                    <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${actionColor}`}>
                                      <ActionIcon className="h-4 w-4" aria-hidden="true" />
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-2">
                                        <p className="text-xs font-medium text-gray-900">
                                          {entry.action_display}
                                        </p>
                                        {entry.user_name && (
                                          <div className="flex items-center text-xs text-gray-500">
                                            <UserIcon className="h-3 w-3 mr-1" />
                                            {entry.user_name}
                                          </div>
                                        )}
                                      </div>
                                      <time className="text-xs text-gray-500">
                                        {formatDate(entry.created_at)}
                                      </time>
                                    </div>
                                    
                                    <div className="mt-1 text-sm text-gray-700">
                                      <p>{entry.description}</p>
                                    </div>

                                    {/* Mudanças de Status e Localização */}
                                    {(entry.old_status && entry.new_status) && (
                                      <div className="mt-2 text-xs text-gray-500">
                                        <span className="inline-flex items-center">
                                          Status: {entry.old_status} 
                                          <ArrowRightIcon className="h-3 w-3 mx-1" />
                                          {entry.new_status}
                                        </span>
                                      </div>
                                    )}
                                    
                                    {(entry.old_location && entry.new_location) && (
                                      <div className="mt-1 text-xs text-gray-500">
                                        <span className="inline-flex items-center">
                                          <MapPinIcon className="h-3 w-3 mr-1" />
                                          Localização: {entry.old_location} 
                                          <ArrowRightIcon className="h-3 w-3 mx-1" />
                                          {entry.new_location}
                                        </span>
                                      </div>
                                    )}

                                    {entry.contract_reference && (
                                      <div className="mt-1 text-xs text-gray-500">
                                        <span className="inline-flex items-center">
                                          <DocumentTextIcon className="h-3 w-3 mr-1" />
                                          Contrato: {entry.contract_reference}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Rodapé */}
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={onClose}
                  >
                    Fechar
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}